/**
 * Divide Fractions Using Reciprocal generator (6.PAFR.3.2).
 *
 * Division of positive fractions (and whole numbers / mixed numbers) by
 * rewriting as multiplication by the reciprocal. The dividend and divisor are
 * exact Rationals, so the quotient is computed exactly and displayed in lowest
 * terms. The "multiply by the reciprocal" transformation is shown as a step.
 *
 * Families:
 *   F1: (a/b) / (c/d)   fraction by fraction
 *   F2: w / (c/d)       whole number by fraction
 *   F3: (a/b) / w       fraction by whole number
 *   F4: mixed / mixed   mixed numbers (converted to improper first)
 */

import { rat, type Rational } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "divide-fractions" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3" | "F4";

interface Cfg {
  readonly families: readonly Family[];
  readonly denomMax: number;
  readonly numerMax: number;
  readonly wholeMax: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["F1", "F3"], denomMax: 6, numerMax: 5, wholeMax: 6 },
  standard: { families: ["F1", "F2", "F3"], denomMax: 9, numerMax: 8, wholeMax: 9 },
  hard: { families: ["F1", "F2", "F3", "F4"], denomMax: 12, numerMax: 11, wholeMax: 10 },
  challenge: { families: ["F1", "F2", "F4"], denomMax: 12, numerMax: 11, wholeMax: 12 },
};

/** A positive proper fraction. */
function makeFraction(rng: Rng, cfg: Cfg): Rational {
  const den = rng.int(2, cfg.denomMax);
  const num = rng.int(1, Math.max(1, Math.min(cfg.numerMax, den - 1)));
  return rat(num, den);
}

/** A positive mixed number as an improper Rational, plus its display parts. */
function makeMixed(rng: Rng, cfg: Cfg): { value: Rational; whole: number; num: number; den: number } {
  const whole = rng.int(1, 4);
  const den = rng.int(2, cfg.denomMax);
  const num = rng.int(1, den - 1);
  return { value: rat(whole * den + num, den), whole, num, den };
}

export class DivideFractionsGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);

      let a: Rational;
      let b: Rational;
      let dividendLatex: string;
      let dividendText: string;
      let divisorLatex: string;
      let divisorText: string;

      switch (family) {
        case "F1": {
          a = makeFraction(rng, cfg);
          b = makeFraction(rng, cfg);
          dividendLatex = a.toLatex();
          dividendText = a.toString();
          divisorLatex = b.toLatex();
          divisorText = b.toString();
          break;
        }
        case "F2": {
          const w = rng.int(2, cfg.wholeMax);
          a = rat(w);
          b = makeFraction(rng, cfg);
          dividendLatex = a.toLatex();
          dividendText = a.toString();
          divisorLatex = b.toLatex();
          divisorText = b.toString();
          break;
        }
        case "F3": {
          a = makeFraction(rng, cfg);
          const w = rng.int(2, cfg.wholeMax);
          b = rat(w);
          dividendLatex = a.toLatex();
          dividendText = a.toString();
          divisorLatex = b.toLatex();
          divisorText = b.toString();
          break;
        }
        case "F4": {
          const m1 = makeMixed(rng, cfg);
          const m2 = makeMixed(rng, cfg);
          a = m1.value;
          b = m2.value;
          dividendLatex = `${m1.whole}\\frac{${m1.num}}{${m1.den}}`;
          dividendText = `${m1.whole} ${m1.num}/${m1.den}`;
          divisorLatex = `${m2.whole}\\frac{${m2.num}}{${m2.den}}`;
          divisorText = `${m2.whole} ${m2.num}/${m2.den}`;
          break;
        }
      }

      const answer = a.div(b);

      const steps = [
        {
          description: "Rewrite division as multiplication by the reciprocal of the divisor.",
          latex: `${a.toLatex()} \\times ${b.reciprocal().toLatex()}`,
        },
        {
          description: "Multiply and reduce to lowest terms.",
          latex: `= ${answer.toLatex()}`,
        },
      ];

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: {
          text: `Divide (lowest terms): ${dividendText} / ${divisorText}`,
          latex: `${dividendLatex} \\div ${divisorLatex} = \\;?`,
        },
        answerType: "number",
        answer: { kind: "number", value: answer },
        steps,
        hints: [
          { level: 1, text: "Dividing by a fraction is the same as multiplying by its reciprocal." },
          { level: 2, text: "Flip the second number, change divide to multiply, then reduce." },
        ],
        subskills: topic.subskills,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "number" ? q.answer.value.key() : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const divideFractions = new DivideFractionsGenerator();
